import React from "react";
import { useClinicalNotesModule, PRESET_SYMPTOMS } from "./hooks/clinical_notes_module.hook";
import style from "./clinical_notes.module.css";

export const DoctorClinicalNotes: React.FC = () => {
  const {
    consultation,
    isSaving,
    isUploadingFile,
    chiefComplaint,
    duration,
    selectedSymptoms,
    customSymptom,
    showCustomInput,
    patientHistory,
    diagnosis,
    dosAndDonts,
    hospitalizationRecommended,
    ambulanceType,
    additionalNotes,
    notesFile,
    notesPreview,
    setChiefComplaint,
    setDuration,
    setCustomSymptom,
    setShowCustomInput,
    setPatientHistory,
    setDiagnosis,
    setDosAndDonts,
    setHospitalizationRecommended,
    setAmbulanceType,
    setAdditionalNotes,
    setNotesFile,
    setNotesPreview,
    handleToggleSymptom,
    handleAddCustomSymptom,
    handleNotesFileChange,
    handleSave,
    handleBack,
    toast,
  } = useClinicalNotesModule();

  return (
    <div className={style.doctorBody}>
      {toast.show && (
        <div className={`${style.toastMsg} ${style.show} ${style[toast.type]}`}>
          <span className="material-icons-round">
            {toast.type === "success" ? "check_circle" : "error"}
          </span>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className={style.docHeader}>
        <button className={style.docHeaderBack} onClick={handleBack}>
          <span className="material-icons-round">arrow_back</span>
        </button>
        <div className={style.docHeaderTitle}>
          <h1>Clinical Notes</h1>
          <span>{consultation?.ticket_id || "TKT-N/A"}</span>
        </div>
        <img
          src="/logo-transparent3.png"
          alt="Pococare"
          style={{ height: "36px" }}
          onError={(e) => {
            (e.target as HTMLImageElement).src =
              "https://nice-stone-091e25e00.3.azurestaticapps.net/logo-transparent3.png";
          }}
        />
      </div>

      <div className={style.docContent}>
        {/* Patient Context */}
        <div className={style.patientContext}>
          <span className="material-icons-round">person</span>
          <div className={style.patientContextInfo}>
            <div className={style.patientContextName}>
              {consultation?.patient_name || "Patient"} •{" "}
              {consultation?.patient_age || "N/A"}/{consultation?.patient_gender ? consultation.patient_gender.charAt(0) : "N/A"}
            </div>
            <div className={style.patientContextMeta}>
              Ticket: {consultation?.ticket_id || "N/A"} • Video Consultation
            </div>
          </div>
        </div>

        {/* Chief Complaint */}
        <div className={style.formGroup}>
          <label>Chief Complaint <span style={{ color: "var(--error)" }}>*</span></label>
          <textarea
            placeholder="Describe the primary complaint..."
            value={chiefComplaint}
            onChange={(e) => setChiefComplaint(e.target.value)}
          />
        </div>

        {/* Duration */}
        <div className={style.formGroup}>
          <label>Duration of Symptoms</label>
          <input
            type="text"
            placeholder="e.g., 30 minutes, 2 days"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
          />
        </div>

        {/* Symptoms */}
        <div className={style.formGroup}>
          <label>Symptoms</label>
          <div className={style.chipsContainer}>
            {PRESET_SYMPTOMS.map((symptom) => {
              const isSelected = selectedSymptoms.includes(symptom);
              return (
                <span
                  key={symptom}
                  className={`${style.chip} ${isSelected ? style.selected : ""}`}
                  onClick={() => handleToggleSymptom(symptom)}
                >
                  {isSelected && <span className="material-icons-round">check</span>}
                  {symptom}
                </span>
              );
            })}
            {selectedSymptoms
              .filter((s) => !PRESET_SYMPTOMS.includes(s))
              .map((symptom) => (
                <span
                  key={symptom}
                  className={`${style.chip} ${style.selected}`}
                  onClick={() => handleToggleSymptom(symptom)}
                >
                  <span className="material-icons-round">check</span>
                  {symptom}
                </span>
              ))}
            {showCustomInput ? (
              <div style={{ display: "flex", gap: "6px", width: "100%" }}>
                <input
                  type="text"
                  placeholder="Custom symptom"
                  value={customSymptom}
                  onChange={(e) => setCustomSymptom(e.target.value)}
                  style={{ padding: "8px 12px", fontSize: "13px", height: "36px", flex: 1 }}
                />
                <button
                  className={style.btnSave}
                  style={{ padding: "8px 16px", height: "36px", flex: "none" }}
                  onClick={handleAddCustomSymptom}
                >
                  Add
                </button>
              </div>
            ) : (
              <span className={`${style.chip} ${style.chipAdd}`} onClick={() => setShowCustomInput(true)}>
                <span className="material-icons-round">add</span>
                Add Custom
              </span>
            )}
          </div>
        </div>

        {/* Patient History */}
        <div className={style.formGroup}>
          <label>Patient History</label>
          <textarea
            placeholder="Relevant medical history..."
            value={patientHistory}
            onChange={(e) => setPatientHistory(e.target.value)}
          />
        </div>

        {/* Diagnosis */}
        <div className={style.formGroup}>
          <label>Provisional Diagnosis</label>
          <textarea
            placeholder="Your assessment..."
            style={{ minHeight: "60px" }}
            value={diagnosis}
            onChange={(e) => setDiagnosis(e.target.value)}
          />
        </div>

        {/* Dos & Don'ts */}
        <div className={style.formGroup}>
          <label>Dos and Don'ts</label>
          <textarea
            placeholder="Instructions for the patient..."
            value={dosAndDonts}
            onChange={(e) => setDosAndDonts(e.target.value)}
          />
        </div>

        {/* Recommendations */}
        <div className={style.recommendationSection}>
          <h3>Recommendations</h3>
          <div className={style.toggleRow}>
            <label>
              <span className="material-icons-round">local_hospital</span>
              Hospitalisation Recommended
            </label>
            <div
              className={`${style.toggleSwitch} ${hospitalizationRecommended ? style.on : ""}`}
              onClick={() => setHospitalizationRecommended(!hospitalizationRecommended)}
            />
          </div>
          <div>
            <label style={{
              fontSize: "11px", fontWeight: 700, color: "var(--on-surface-variant)",
              textTransform: "uppercase", letterSpacing: "0.3px", display: "block", marginBottom: "8px",
            }}>
              Ambulance Type
            </label>
            <div className={style.radioGroup}>
              {(["ALS", "BLS", "None"] as const).map((type) => (
                <div
                  key={type}
                  className={`${style.radioOption} ${ambulanceType === type ? style.selected : ""}`}
                  onClick={() => setAmbulanceType(type)}
                >
                  {type === "ALS" ? "ALS (Advanced)" : type === "BLS" ? "BLS (Basic)" : "None"}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Additional Notes */}
        <div className={style.formGroup}>
          <label>Additional Notes</label>
          <textarea
            placeholder="Any other observations..."
            style={{ minHeight: "60px" }}
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
          />
        </div>

        {/* Upload Handwritten Notes */}
        <div className={style.formGroup}>
          <label>
            Upload Handwritten Notes{" "}
            <span style={{ color: "var(--on-surface-variant)", textTransform: "none", fontSize: "11px", fontWeight: 500 }}>
              (Optional)
            </span>
          </label>
          <input
            type="file"
            id="notes-camera-upload-input"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={handleNotesFileChange}
          />
          <input
            type="file"
            id="notes-file-upload-input"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleNotesFileChange}
          />
          <div className={style.uploadActions} style={{ marginBottom: "12px" }}>
            <div className={style.uploadBtn} onClick={() => document.getElementById("notes-camera-upload-input")?.click()}>
              <div className={style.uploadBtnIcon}><span className="material-icons-round">camera_alt</span></div>
              <span className={style.uploadBtnLabel}>Camera</span>
              <span className={style.uploadBtnHint}>Capture notes</span>
            </div>
            <div className={style.uploadBtn} onClick={() => document.getElementById("notes-file-upload-input")?.click()}>
              <div className={style.uploadBtnIcon}><span className="material-icons-round">photo_library</span></div>
              <span className={style.uploadBtnLabel}>Gallery / File</span>
              <span className={style.uploadBtnHint}>JPG, PNG</span>
            </div>
          </div>
          {notesFile && (
            <div className={style.imagePreview} style={{ marginTop: "12px" }}>
              <div className={style.previewHeader}>
                <span className={style.previewHeaderTitle}>{notesFile.name}</span>
                <button
                  className={style.previewRemove}
                  onClick={(e) => { e.stopPropagation(); setNotesFile(null); setNotesPreview(null); }}
                >
                  <span className="material-icons-round">delete</span>
                  Remove
                </button>
              </div>
              {notesPreview ? (
                <img className={style.previewImg} src={notesPreview} alt="Preview" />
              ) : (
                <div style={{ padding: "40px 16px", textAlign: "center", color: "var(--placeholder)" }}>
                  <span className="material-icons-round" style={{ fontSize: "48px" }}>image</span>
                  <p style={{ marginTop: "8px", fontSize: "13px" }}>File Ready</p>
                </div>
              )}
              <div className={style.previewFooter}>
                <span className="material-icons-round">check_circle</span>
                Ready to upload
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Action */}
      <div className={style.bottomAction}>
        <button className={style.btnBackCall} onClick={handleBack}>
          <span className="material-icons-round" style={{ fontSize: "18px" }}>arrow_back</span>
          Back
        </button>
        <button className={style.btnSave} onClick={handleSave} disabled={isSaving || isUploadingFile}>
          <span className="material-icons-round" style={{ fontSize: "18px" }}>check</span>
          {isUploadingFile ? "Uploading..." : isSaving ? "Saving..." : "Save Notes"}
        </button>
      </div>
    </div>
  );
};
